import type {
  BatchArgs,
  BatchResponse,
  Play,
  PlayCreate,
  PlayData,
  Sort,
} from '@carboncopyco/types';
import { removeUndefinedValues } from '@carboncopyco/utils';
import type { EndpointDefinitions } from '@reduxjs/toolkit/query';
import type { QueryKeys } from '@reduxjs/toolkit/src/query/core/apiState';
import type { UserFilter } from '../../types';
import { handleBatchRequest } from '../utils';
import { queryDataUpdateHandler } from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';

export type GetPlayFilterResponse = {
  creators: UserFilter[];
  companies?: string[];
};

export type GetPlayFiltersArgs = {
  organizationId: string;
  assetId: string;
  assetType: string;
  workspaces?: string[];
  includeArchivedWorkspaces?: boolean;
  query: string;
  deletedEntities?: boolean;
};

export interface GetPlaysArgs extends BatchArgs {
  organizationId: string;
  assetId: string;
  assetType: string;
  deletedEntities?: boolean;
  companies?: string[];
  creators?: string[];
  workspaces?: string[];
}

interface GetQueuedPlaysArgs extends BatchArgs<Sort> {
  organizationId: string;
  limit: number;
  deletedEntities?: boolean;
}

export type GetPlayResponse = Play;

export type BasePlayArgs = {
  playId: string;
  organizationId: string;
  removeDeleted?: boolean;
};

const playApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    createPlay: builder.mutation<Play, PlayCreate>({
      query: ({
        organizationId,
        assetType,
        assetId,
        settings,
        assets,
        workspaceId,
      }) => ({
        url: `/api/${organizationId}/play/new`,
        method: 'POST',
        body: removeUndefinedValues({
          assetType,
          assetId,
          settings,
          assets,
          workspaceId,
        }),
      }),
      invalidatesTags: (res, _, args) =>
        res
          ? args.settings?.playLater
            ? [
                { type: 'Plays' },
                { type: 'Presentation' },
                { type: 'Presentations' },
                {
                  type: res.folderId ? 'FolderAssets' : 'WorkspaceAssets',
                  id: res.folderId || res.workspaceId,
                },
                { type: 'QueuedPlays' },
              ]
            : [
                { type: 'Plays' },
                { type: 'Presentation' },
                { type: 'Presentations' },
                {
                  type: res.folderId ? 'FolderAssets' : 'WorkspaceAssets',
                  id: res.folderId || res.workspaceId,
                },
              ]
          : [
              { type: 'Plays' },
              { type: 'Presentation' },
              { type: 'Presentations' },
            ],
    }),
    getQueuedPlays: builder.query<BatchResponse<Play>, GetQueuedPlaysArgs>({
      query: ({ organizationId, deletedEntities, limit, ...rest }) =>
        handleBatchRequest({
          url: `/api/${organizationId}/play/search-all`,
          params: removeUndefinedValues({
            deletedEntities,
            playLater: true,
          }),
          limit,
          ...rest,
        }),
      providesTags: ['QueuedPlays'],
    }),
    getPlays: builder.query<BatchResponse<Play>, GetPlaysArgs>({
      query: ({
        organizationId,
        assetId,
        assetType,
        deletedEntities,
        companies,
        creators,
        workspaces,
        ...rest
      }) =>
        handleBatchRequest({
          ...rest,
          url: `/api/${organizationId}/play/search`,
          params: removeUndefinedValues({
            assetId,
            assetType,
            workspaces: workspaces?.join(','),
            deletedEntities,
            companies: companies?.join(','),
            creators: creators?.join(','),
          }),
        }),
      providesTags: (_, __, { assetId, deletedEntities }) =>
        deletedEntities
          ? [{ type: 'ArchivedPlays', id: assetId }]
          : [{ type: 'Plays', id: assetId }],
    }),
    getPlaysFilters: builder.query<GetPlayFilterResponse, GetPlayFiltersArgs>({
      query: ({
        organizationId,
        assetId,
        query,
        deletedEntities,
        assetType,
        workspaces,
      }) => ({
        url: `/api/${organizationId}/play/search/filters`,
        params: removeUndefinedValues({
          assetId,
          assetType,
          query,
          deletedEntities,
          workspaces: workspaces?.join(','),
        }),
      }),
    }),
    getPlayData: builder.query<PlayData, BasePlayArgs>({
      query: ({ organizationId, playId, removeDeleted = false }) => ({
        url: `/api/${organizationId}/play/${playId}/play-data`,
        params: { removeDeleted: String(removeDeleted) },
      }),
      providesTags: (_, __, { playId }) => [{ type: 'PlayData', id: playId }],
    }),
    deletePlay: builder.mutation<Play, BasePlayArgs>({
      query: ({ organizationId, playId }) => ({
        url: `/api/${organizationId}/play/${playId}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (_arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: archivedPlay } = await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            state,
            dispatch,
            endpointName: 'getPlays' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'assetId',
            endpointArgumentValue: archivedPlay.asset.assetId,
            updateQueryDataFunction: (plays) => ({
              ...plays,
              items: plays.items.filter((play) => play.id !== archivedPlay.id),
            }),
          });
        } catch (error) {
          console.error(error);
        }
      },
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'Playbook' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
              { type: 'Plays' },
              { type: 'ArchivedPlays' },
            ]
          : [{ type: 'Plays' }, { type: 'ArchivedPlays' }],
    }),
    restorePlay: builder.mutation<Play, BasePlayArgs>({
      query: ({ organizationId, playId }) => ({
        url: `/api/${organizationId}/play/${playId}/restore`,
        method: 'POST',
      }),
      onQueryStarted: async (_arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: restoredPlay } = await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            state,
            dispatch,
            endpointName: 'getPlays' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'assetId',
            endpointArgumentValue: restoredPlay.asset.assetId,
            updateQueryDataFunction: (plays) => ({
              ...plays,
              items: plays.items.filter((play) => play.id !== restoredPlay.id),
            }),
          });
        } catch (error) {
          console.error(error);
        }
      },
      invalidatesTags: (res) =>
        res
          ? [
              { type: 'Playbook' },
              {
                type: 'WorkspaceAssets',
                id: res.workspaceId,
              },
              { type: 'FolderAssets', id: res.folderId },
              { type: 'Plays' },
              { type: 'ArchivedPlays' },
            ]
          : [{ type: 'Plays' }, { type: 'ArchivedPlays' }],
    }),
    getPlayToken: builder.query<{ token: string }, BasePlayArgs>({
      query: ({ organizationId, playId }) => ({
        url: `/api/${organizationId}/play/${playId}/token`,
        method: 'POST',
      }),
    }),
    getPlay: builder.query<GetPlayResponse, BasePlayArgs>({
      query: ({ organizationId, playId, removeDeleted = false }) => ({
        url: `/api/${organizationId}/play/${playId}`,
        params: { removeDeleted: String(removeDeleted) },
      }),
    }),
  }),
});

export const {
  useCreatePlayMutation,
  useGetQueuedPlaysQuery,
  useGetPlaysQuery,
  useLazyGetPlaysQuery,
  useLazyGetPlaysFiltersQuery,
  useLazyGetPlayDataQuery,
  useLazyGetPlayTokenQuery,
  useDeletePlayMutation,
  useRestorePlayMutation,
  useLazyGetPlayQuery,
} = playApi;
